import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { QuestionCard } from '@/components/QuestionCard';
import { ProgressBar } from '@/components/ProgressBar';
import { storage } from '@/lib/storage';
import { ProductivityQuestion } from '@/lib/api';
import { funnelAnalytics } from '@/services/funnel-analytics';

export default function QuestionsPage() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<ProductivityQuestion[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});

  useEffect(() => {
    // Load questions from storage
    const savedQuestions = storage.getQuestions();
    if (!savedQuestions || savedQuestions.length === 0) {
      // No questions - redirect back to goals
      navigate('/goals');
      return;
    }

    setQuestions(savedQuestions);

    // Track funnel step: Questions started
    funnelAnalytics.trackQuestionsStarted(savedQuestions.length);

    // Load saved answers if any
    const savedAnswers = storage.getAnswers();
    setAnswers(savedAnswers);

    storage.setCurrentStep('questions');
  }, [navigate]);

  const handleAnswerChange = (questionId: string, answer: string) => {
    const newAnswers = { ...answers, [questionId]: answer };
    setAnswers(newAnswers);
    storage.setAnswers(newAnswers);
  };

  const handleNext = () => {
    // Track this question as answered (1-indexed for display)
    const questionNumber = currentIndex + 1;
    if (questionNumber <= 5) {
      funnelAnalytics.trackQuestionAnswered(questionNumber);
    }

    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      // All questions answered - go to email capture
      // Track funnel step: Questions completed
      funnelAnalytics.trackQuestionsCompleted(questions.length);

      storage.setCurrentStep('email');
      navigate('/email');
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  if (questions.length === 0) {
    return null;
  }

  const currentQuestion = questions[currentIndex];
  const currentAnswer = answers[currentQuestion.id] || '';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="section">
        <div className="container-tight">
          {/* Progress */}
          <div className="mb-8">
            <ProgressBar current={2} total={3} />
          </div>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <h1 className="heading">Tell Us More About Your Goals</h1>
            <p className="subheading">
              Answer these questions to help us create your personalized productivity plan
            </p>
          </motion.div>

          {/* Question Progress */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                Question {currentIndex + 1} of {questions.length}
              </span>
              <span className="text-sm text-gray-500">
                {Math.round(((currentIndex + 1) / questions.length) * 100)}%
              </span>
            </div>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
              />
            </div>
          </div>

          {/* Question Card with Animation */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentQuestion.id}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.3 }}
            >
              <QuestionCard
                question={currentQuestion}
                answer={currentAnswer}
                onChange={(answer) => handleAnswerChange(currentQuestion.id, answer)}
                onNext={handleNext}
                onPrevious={handlePrevious}
                isFirst={currentIndex === 0}
                isLast={currentIndex === questions.length - 1}
              />
            </motion.div>
          </AnimatePresence>

          {/* Answer Summary (mobile-friendly dots) */}
          <div className="mt-8 flex items-center justify-center gap-2">
            {questions.map((q, index) => (
              <button
                key={q.id}
                onClick={() => setCurrentIndex(index)}
                className={`w-3 h-3 rounded-full transition-all ${
                  index === currentIndex
                    ? 'bg-primary-600 scale-125'
                    : answers[q.id]
                    ? 'bg-success-500'
                    : 'bg-gray-300'
                }`}
                aria-label={`Go to question ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
